import CallIcon from '@mui/icons-material/Call';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Container,
  Grid,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const EFTPS = () => {
  return (
    <AppLayout
      meta={
        <Meta
          titleTemplate="Learn about the IRS EFTPS tax payment method"
          description="Simplify your tax payments with our comprehensive EFTPS guide. Learn how to effortlessly navigate the system, avoid costly mistakes, and stay compliant"
          keywords=""
          canonical="resources/eftps/"
          ogType="article"
          // jsonLdPricing="11.99"
          // jsonLdAggregateRatingValue="4.95"
          // jsonLdAggregateRatingCount="4278"
        />
      }
    >
      <Container maxWidth="lg">
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 5,
            mt: 5,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight">EFTPS</span>
          </span>{' '}
          Payment Method
        </Typography>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/eftps/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-08-10T09:00:00-05:00"
                      >
                        August 10, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-08-10T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        August 10, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Alexia Zepeda
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    EFTPS at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemProp="articleBody"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>
                          EFTPS (Electronic Federal Tax Payment System)
                        </strong>{' '}
                        is a free electronic payment service provided by the{' '}
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          U.S. Department of the Treasury
                        </strong>{' '}
                        that allows individuals and businesses to pay federal
                        taxes electronically from a bank account. It can be used
                        to make and schedule various federal tax payments
                        securely online or by phone.
                      </span>
                    </li>
                    <li>
                      <span>
                        Individuals, businesses, tax-exempt organizations, and
                        federal agencies can use EFTPS to make federal tax
                        payments.
                      </span>
                    </li>

                    <li>
                      <span>
                        EFTPS can be used for{' '}
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          income, employment, estimated, excise, corporate,
                          gift, estate, and other federal taxes
                        </strong>
                        .
                      </span>
                    </li>
                    <li>
                      <span>
                        Payments are made electronically from a{' '}
                        <strong style={{ fontWeight: 600 }}>
                          bank account
                        </strong>{' '}
                        through the EFTPS system.
                      </span>
                    </li>

                    <li>
                      <span>
                        EFTPS is available{' '}
                        <strong style={{ fontWeight: 600 }}>
                          24 hours a day, 365 days a year
                        </strong>{' '}
                        for scheduling and managing payments.
                      </span>
                    </li>
                    <li>
                      <span>
                        Taxpayers can{' '}
                        <strong style={{ fontWeight: 600 }}>
                          {' '}
                          schedule payments in advance{' '}
                        </strong>{' '}
                        and set up recurring payments for eligible tax
                        obligations.
                      </span>
                    </li>
                  </Box>

                  {/* Publisher needs an Organization with a logo ImageObject — no meta tags, same pattern */}
                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>
                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  What is EFTPS?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  What is EFTPS? The Electronic Federal Tax Payment System
                  <b>(EFTPS)</b> is a free electronic tax payment service
                  provided by the U.S. Treasury that lets you pay federal taxes
                  electronically online or by phone from your bank account. Over
                  12 million individuals, businesses, & other entities use{' '}
                  <Link
                    href="https://www.irs.gov/payments/eftps-the-electronic-federal-tax-payment-system"
                    sx={{ color: '#0202ff' }}
                    target="_blank"
                  >
                    EFTPS
                  </Link>{' '}
                  to pay income, payroll, estimated, excise taxes, & more safely
                  & easily.
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  You can use it anytime, any day of the year, 365 days a year,
                  24/7
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Who can use EFTPS to pay taxes?
                </Typography>

                <Typography variant="body1">
                  EFTPS, the Electronic Federal Tax Payment System, is available
                  for
                </Typography>

                <Typography component="div" sx={{ mb: 4 }}>
                  <ul>
                    <li style={{ marginBottom: 16 }}>
                      <b>Businesses, individuals</b> (such as freelancers or
                      self-employed individuals)
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      <b>Tax-exempt organizations</b> (like charities)
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      <b>Federal agencies</b> to pay their federal taxes
                      electronically. It&apos;s a convenient and secure way to
                      make payments directly to the IRS
                    </li>
                  </ul>
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  What types of federal taxes can you pay with EFTPS?
                </Typography>

                <Typography variant="body1">
                  With EFTPS, you can pay different types of federal taxes
                  online, including:
                </Typography>

                <Typography component="div" sx={{ mb: 4 }}>
                  <ul>
                    <li style={{ marginBottom: 16 }}>
                      <b>Income Taxes:</b> For individuals and businesses.
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      <b>Employment Taxes:</b> Taxes withheld from
                      employees&apos; paychecks.
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      <b>Excise Taxes:</b> Taxes on specific goods like gasoline
                      and alcohol.
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      <b>Estimated Taxes:</b> Payments made throughout the year
                      if you expect to owe taxes.
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      <b>Gift and Estate Taxes:</b> For those subject to these
                      taxes.
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      <b>Other Taxes:</b> Like penalties and interest payments.
                    </li>
                  </ul>
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  TaxZerone also supports the EFTPS payment method for the
                  following forms:
                </Typography>

                <Typography variant="body1" component="div" sx={{ mb: 4 }}>
                  <ul>
                    <li style={{ marginBottom: 16 }}>
                      <b>
                        <Link
                          href="/excise-tax/efile-irs-form-2290-heavy-vehicle-use-tax-online/"
                          sx={{ color: '#0202ff' }}
                        >
                          Form 2290
                        </Link>{' '}
                        (Excise Tax Forms)
                      </b>{' '}
                      - Used for reporting heavy highway vehicle use tax (HVUT).
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      <b>
                        <Link
                          href="/employment-tax/e-file-irs-form-940/"
                          sx={{ color: '#0202ff' }}
                        >
                          Form 940
                        </Link>{' '}
                        (Employment Tax Forms)
                      </b>{' '}
                      - Reports federal unemployment tax payments.
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      <b>
                        <Link
                          href="/employment-tax/e-file-irs-form-941/"
                          sx={{ color: '#0202ff' }}
                        >
                          Form 941
                        </Link>{' '}
                        (Employment Tax Forms)
                      </b>{' '}
                      - Used to report quarterly employment tax returns.
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      <b>Form 1120-POL (Exempt Org. Forms)</b> - Used by
                      political organizations to report taxable income.
                    </li>
                  </ul>
                </Typography>
                <Box
                  component="div"
                  sx={{
                    mb: 5,
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#f9f9f9',
                    backgroundSize: {
                      xs: '20% auto',
                      sm: '20% auto',
                      md: '20% auto',
                    },
                    border: '1px solid #ccc',
                    // color: '#fff',
                  }}
                  // className="wave-bg"
                >
                  <Box sx={{ width: '100%' }}>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 600,
                        mb: 1,
                        fontSize: '24px !important',
                      }}
                    >
                      Easily E-file IRS tax forms using EFTPS for a hassle-free
                      tax experience.
                    </Typography>
                    <Typography variant="body2">
                      Quick and efficient electronic filing at affordable
                      pricing
                    </Typography>
                  </Box>
                  <Box sx={{ width: '100%', textAlign: 'left', mt: 2 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      sx={{
                        textTransform: 'none',
                        boxShadow: 'none',
                        borderRadius: 2,
                      }}
                      size="large"
                      href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=eftps`}
                    >
                      Start Filing
                    </Button>
                  </Box>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  How do you enroll in EFTPS online?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  To enroll in EFTPS online, visit the official EFTPS website
                  provided by the Internal Revenue Service (IRS). Here&apos;s
                  how:
                </Typography>
                <Typography variant="body1" component="div" sx={{ mb: 2 }}>
                  <ol>
                    <li style={{ marginBottom: 16 }}>
                      Visit the official{' '}
                      <Link
                        href="https://www.eftps.gov/eftps/"
                        target="_blank"
                        sx={{ color: '#0202ff' }}
                      >
                        EFTPS website
                      </Link>
                      .
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      Click on &rdquo;Enroll&ldquo; and follow the provided
                      instructions.
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      Provide your personal, business, and banking details.
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      Receive your PIN via mail.
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      Log in using your TIN and the PIN.
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      Once logged in, schedule tax payments and manage your
                      account.
                    </li>
                  </ol>
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Keep your PIN secure and contact EFTPS customer service for
                  assistance if needed.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  What payment options does EFTPS offer?
                </Typography>

                <Typography variant="body1" sx={{ mb: 4 }}>
                  EFTPS offers payment options including direct bank transfers,
                  scheduled ACH debits, batch payments for businesses,
                  credit/debit card payments (with fees), and same-day wire
                  transfers via FedTax.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  How do I get an EFTPS payment receipt?
                </Typography>
                <Typography variant="body1" component="div" sx={{ mb: 2 }}>
                  <ol>
                    <li style={{ marginBottom: 16 }}>
                      Log in to your{' '}
                      <Link
                        href="https://www.eftps.gov/eftps/"
                        target="_blank"
                        sx={{ color: '#0202ff' }}
                      >
                        EFTPS account
                      </Link>
                      .
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      Navigate to the &rdquo;Payment History&ldquo; section.
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      Locate the payment for which you need a receipt.
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      Click to view or download the receipt.
                    </li>
                  </ol>
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  If you need help, you can always contact EFTPS{' '}
                  <Link
                    href="https://www.eftps.gov/eftps/direct/ContactUs.page"
                    target="_blank"
                    sx={{ color: '#0202ff' }}
                  >
                    Customer Service
                  </Link>
                  .
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Can state taxes be paid through EFTPS?
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  No, EFTPS is only for federal tax payments. State taxes must
                  be paid through the respective state&apos;s payment system.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Is EFTPS secure?
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Yes, EFTPS (Electronic Federal Tax Payment System) meets
                  high-security standards, using encryption and verification
                  procedures to protect taxpayer information and ensure secure
                  transactions.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  What is the difference between the EFTPS and IRS Direct Pay?
                </Typography>
                <Typography variant="body1" component="div" sx={{ mb: 4 }}>
                  <b>EFTPS (Electronic Federal Tax Payment System):</b>
                  <ul>
                    <li style={{ marginBottom: 16 }}>
                      Also free, but you need to sign up first.
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      Allows for a wider range of tax payments, including
                      business taxes.
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      More flexible scheduling, up to 365 days in advance.
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      Best for businesses and people with more complex tax
                      situations.
                    </li>
                  </ul>
                </Typography>
                <Typography variant="body1" component="div" sx={{ mb: 4 }}>
                  <b>IRS Direct Pay:</b>
                  <ul>
                    <li style={{ marginBottom: 16 }}>
                      Easy and free way to pay taxes online directly from your
                      bank account.
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      No need to sign up, just login and pay.
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      Good for individual income taxes and simple payments.
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      You can pay immediately or schedule payments up to 30 days
                      ahead.
                    </li>
                  </ul>
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Do businesses need a separate EFTPS account?
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  <b>Yes,</b> businesses typically need a separate <b>EFTPS</b>{' '}
                  account to make federal tax payments. This allows for proper
                  tracking and management of tax payments, separating business
                  tax obligations from personal ones. Each entity, including
                  businesses, must register for its own EFTPS account to ensure
                  accurate reporting and payment processing.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  What happens if an EFTPS payment is late?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If an EFTPS (Electronic Federal Tax Payment System) payment is
                  late, the taxpayer may incur penalties and interest charges
                  imposed by the IRS.
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  These penalties and interest accrue on the unpaid tax amount
                  from the due date until the payment is made. The penalty rate
                  can vary depending on the type of tax and the length of the
                  delay. It&apos;s essential to make timely payments to avoid
                  these penalties and minimize additional costs.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  What payment methods does EFTPS support?
                </Typography>
                <Typography variant="body1" component="div" sx={{ mb: 4 }}>
                  <ul>
                    <li style={{ marginBottom: 16 }}>
                      Income taxes (personal and business)
                    </li>
                    <li style={{ marginBottom: 16 }}>Estimated income taxes</li>
                    <li style={{ marginBottom: 16 }}>Payroll taxes</li>
                    <li style={{ marginBottom: 16 }}>Excise taxes</li>
                    <li style={{ marginBottom: 16 }}>Corporate taxes </li>
                  </ul>
                  Easily pay your federal taxes electronically with EFTPS,
                  supported by{' '}
                  <Link href="/" sx={{ color: '#0202ff' }}>
                    TaxZerone
                  </Link>
                  . Covering income, payroll, estimated, excise, and corporate
                  taxes, it simplifies your tax obligations for both individuals
                  and businesses
                </Typography>
                <Box
                  component="div"
                  sx={{ mb: 5, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 0.5 }}
                      >
                        E-file IRS tax forms using the convenient EFTPS option.
                        Simplify your tax filing process today
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 180, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=eftps`}
                      >
                        Get Started Now
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Can you automate recurring EFTPS payments?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b>Yes,</b> you can automate recurring EFTPS payments through
                  the system&apos;s Electronic Funds Withdrawal (EFW) option.
                  Here&apos;s how it generally works:
                </Typography>
                <ol style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 16 }}>
                    <b>Authorize EFTPS:</b> Fill out a form with your bank
                    details and payment frequency to allow EFTPS to withdraw
                    funds from your account.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <b>Schedule Payments:</b> Once authorized, set up recurring
                    payments on EFTPS, specifying how often and how much to pay.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <b>Confirm Schedule:</b> Double-check your payment details
                    for accuracy and confirm to activate your recurring
                    payments.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <b>Monitor and Adjust:</b> Keep an eye on your bank
                    statements and EFTPS activity. You can tweak your payment
                    schedule or cancel recurring payments as needed.
                  </li>
                </ol>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Automating EFTPS payments makes meeting your federal tax
                  obligations easier, ensuring timely payments without manual
                  effort.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Difference Between EFTPS and IRS Direct Pay?
                </Typography>
                <Box component="div" sx={{ mb: 4 }}>
                  <TableContainer
                    sx={{
                      border: '1px solid #828282',
                      borderCollapse: 'collapse',
                      '& td': { py: 0.5 },
                    }}
                  >
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow
                          sx={{
                            backgroundColor: '#ccc',
                          }}
                        >
                          <TableCell
                            sx={{
                              color: '#333',
                              width: 300,
                              borderRight: '1px solid #828282',
                              textAlign: 'left',
                              borderBottom: '1px solid #828282',
                              fontSize: 18,
                              fontWeight: 700,
                            }}
                          >
                            Feature
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#333',
                              width: 300,
                              borderRight: '1px solid #828282',
                              textAlign: 'left',
                              borderBottom: '1px solid #828282',
                              fontSize: 18,
                              fontWeight: 700,
                            }}
                          >
                            EFTPS (Electronic Federal Tax Payment System)
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#333',
                              width: 300,
                              textAlign: 'left',
                              borderBottom: '1px solid #828282',
                              fontSize: 18,
                              fontWeight: 700,
                            }}
                          >
                            IRS Direct Pay
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              textAlign: 'left',
                            }}
                          >
                            Provider
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              textAlign: 'left',
                            }}
                          >
                            U.S. Department of the Treasury
                          </TableCell>
                          <TableCell
                            sx={{
                              borderBottom: '1px solid #828282',
                              textAlign: 'left',
                            }}
                          >
                            Internal Revenue Service (IRS)
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              textAlign: 'left',
                            }}
                          >
                            Types of Taxes
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              textAlign: 'left',
                            }}
                          >
                            Various federal taxes including income, employment,
                            corporate, excise, etc.
                          </TableCell>
                          <TableCell
                            sx={{
                              borderBottom: '1px solid #828282',
                              textAlign: 'left',
                            }}
                          >
                            Individual income taxes, estimated taxes, tax
                            balances due, etc.
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              textAlign: 'left',
                            }}
                          >
                            Enrollment Required
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              textAlign: 'left',
                            }}
                          >
                            Yes
                          </TableCell>
                          <TableCell
                            sx={{
                              borderBottom: '1px solid #828282',
                              textAlign: 'left',
                            }}
                          >
                            No
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              textAlign: 'left',
                            }}
                          >
                            Account Setup
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              textAlign: 'left',
                            }}
                          >
                            Yes, taxpayers need to enroll and set up an account
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            Not required, taxpayers can use it without
                            additional registration
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              textAlign: 'left',
                            }}
                          >
                            Payment Scheduling
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              textAlign: 'left',
                            }}
                          >
                            Yes, allows for scheduling payments in advance and
                            setting up recurring payments
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            No, payments are one-time and cannot be scheduled or
                            recurring
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              textAlign: 'left',
                            }}
                          >
                            Payment History Viewing
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                              textAlign: 'left',
                            }}
                          >
                            Yes, users can view their payment history
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            Limited, typically shows recent payment transactions
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              textAlign: 'left',
                            }}
                          >
                            Payment Options
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              textAlign: 'left',
                            }}
                          >
                            Online or by phone
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'left',
                            }}
                          >
                            Online only
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Can sole proprietors enroll in EFTPS as a business?
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  <strong>Yes,</strong> sole proprietors can enroll in EFTPS to
                  make federal tax payments for their businesses. EFTPS is
                  available for various types of businesses, including sole
                  proprietorships. Once enrolled, sole proprietors can use EFTPS
                  to make federal tax payments such as income taxes, estimated
                  taxes, and other business-related taxes.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  How to change a bank account in EFTPS?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  To change your bank account information in EFTPS:
                </Typography>
                <ul style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 16 }}>
                    Log in to your EFTPS account.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Look for the option to update your bank account information.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Enter your new bank account details.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Double-check the information & click to submit.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Allow a few days for the updates to take effect.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Make sure to update any scheduled payments with the new bank
                    account details to avoid payment disruptions.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Confirm that the changes have been successfully applied by
                    checking your account.
                  </li>
                </ul>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Is there a fee to use EFTPS?
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  <b>No,</b> there is typically no fee to use EFTPS. It is a
                  free service provided by the U.S. Department of the Treasury
                  for taxpayers to make electronic payments of their federal
                  taxes.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Your Responsibilities
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  You need to start the process for tax payments. Without your
                  instructions to EFTPS, your payments won&apos;t go through.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  To avoid penalties with EFTPS payments, here&apos;s what you
                  need to do:
                </Typography>
                <ul style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 16 }}>
                    Submit your tax payment to EFTPS by 8 p.m. ET at least one
                    day before the tax due date.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Keep a record of the EFT Acknowledgment Number you get.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Make sure you have enough money in your account to cover the
                    tax payment.
                  </li>
                </ul>

                <Alert
                  severity="warning"
                  icon={false}
                  sx={{
                    borderRadius: 2,
                    mb: 5,
                    '& .MuiAlert-message': {
                      display: 'flex',
                      alignItems: 'center',
                    },
                  }}
                >
                  If EFTPS.gov is unavailable, you still need to make your
                  payments on time by calling{' '}
                  <CallIcon sx={{ mr: 0.5, ml: 1 }} />
                  1.800.555.3453.
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Verify your payment status at any time.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check the status and payment history of any transactions
                  conducted through EFTPS within the last sixteen months on{' '}
                  <Link
                    href="https://www.eftps.gov/eftps/"
                    target="_blank"
                    sx={{ color: '#0202ff' }}
                  >
                    EFTPS.gov
                  </Link>
                  .
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Can I cancel an EFTPS payment?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b>Yes,</b> you can cancel an EFTPS payment as long as it
                  meets certain criteria:
                </Typography>
                <ul style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 16 }}>
                    It must be scheduled for a future date.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    It must be cancelled at least two business days before the
                    scheduled payment date.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    You must cancel the payment through the EFTPS system online
                    or by calling EFTPS Customer Service.
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  If your payment meets these conditions, you can cancel it
                  without incurring any fees or penalties.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Instructions for Foreign Taxpayers
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you&apos;re outside the US but have a US bank account, you
                  can sign up for EFTPS.
                </Typography>
                <ul>
                  <li style={{ marginBottom: 16 }}>
                    To learn more and explore other options for international
                    taxpayers with US tax responsibilities, check out the
                    International Taxpayers Fact Sheet on EFTPS.gov&apos;s
                    Downloads section.
                  </li>
                </ul>
                <Alert
                  severity="info"
                  sx={{
                    mb: 5,
                    borderRadius: 2,
                    '& .MuiTypography-root': { mb: 0 },
                  }}
                >
                  <AlertTitle>
                    Learn more about EFTPS on the{' '}
                    <Link
                      href="https://www.irs.gov/pub/irs-pdf/p966.pdf"
                      target="_blank"
                      sx={{ color: '#0202ff' }}
                    >
                      IRS website
                    </Link>
                    .
                  </AlertTitle>
                </Alert>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This addition clarifies that <b>TaxZerone</b> is a service
                  provider that facilitates electronic tax payments, including
                  those made through EFTPS, for various federal taxes.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              E-File your IRS Forms easily with TaxZerone
              <br /> with EFTPS for quick & hassle-free.
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              File forms online accurately, securely, and at{' '}
              <Link href="/pricing/" sx={{ color: '#0202ff' }}>
                affordable prices
              </Link>{' '}
              with TaxZerone.
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 4,
              }}
            >
              You&apos;ll get IRS return status updates upon processing
            </Typography>
            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=eftps`}
              >
                Get Started Now
              </Button>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Easy. Fast. Precise
              </Typography>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default EFTPS;
